import { Dispatch, SetStateAction, useState } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button } from "@mui/material";
import * as Yup from "yup";
import { isValidInput } from "../utils/validations";
import useTranslation from "../hooks/traslation";
import Loader from "../constants/Loader";

interface AddSkillModalProps {
  open: boolean;
  onClose: () => void;
  name: string;
  selectedId: string;
  setName: Dispatch<SetStateAction<string>>;
  setSelectedId: Dispatch<SetStateAction<string>>;
  handleSubmit: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  isFormLoading?: boolean;
  nameAr: string;
  setNameAr: Dispatch<SetStateAction<string>>;
}

export default function AddSkill({
  open,
  setOpen,
  name,
  setName,
  handleSubmit,
  selectedId,
  setSelectedId,
  onClose,
  isFormLoading = false,
  nameAr,
  setNameAr,
}: AddSkillModalProps) {
  const [errors, setErrors] = useState({
    name: "",
    nameAr: "",
  });
  const translate = useTranslation();

  const skillSchema = Yup.object().shape({
    name: Yup.string()
      .required(translate?.error?.categoryName || "Skill name is required")
      .min(2, translate?.error?.catLimit_max || "Minimum 2 characters")
      .max(50, translate?.error?.catLimit_min || "Maximum 50 characters"),
    nameAr: Yup.string()
      .required(translate?.error?.catName_Arabic || "Arabic name is required")
      .min(2, translate?.error?.minArabicChar_cat || "Minimum 2 characters"),
  });

  const validateForm = async () => {
    try {
      await skillSchema.validate(
        {
          name,
          nameAr,
        },
        { abortEarly: false }
      );
      setErrors({ name: "", nameAr: "" });
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = {
          name: "",
          nameAr: "",
        };
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path as keyof typeof newErrors] = error.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleFormSubmit = async () => {
    if (await validateForm()) {
      handleSubmit();
    }
  };

  const handleClose = () => {
    if (isFormLoading) return; // Prevent closing during loading
    setSelectedId("");
    setOpen(false);
    setErrors({ name: "", nameAr: "" });
  };

  return (
    <Modal
      className="modal addCategory_modal"
      open={open}
      onClose={isFormLoading ? undefined : handleClose}
    >
      <div className="modal-dialog w_900">
        <div className="modal-body">
          <Loader isLoad={isFormLoading} />
          <div className="modal_title">
            <h2>{selectedId ? "Update Skill" : "Add Skill"}</h2>
            <div className="btn-close" onClick={handleClose}>
              <CloseIcon />
            </div>
          </div>
          <form className="form" action="">
            <div className="gap_p">
              <div className="control_group w_50">
                <label>
                  Skill Name {translate?.main?.english || "English"}
                </label>
                <TextField
                  placeholder="Enter skill name"
                  fullWidth
                  value={name}
                  onChange={(e) => {
                    if (isValidInput(e.target.value)) {
                      setName(e.target.value);
                      setErrors((prev) => ({ ...prev, name: "" }));
                    }
                  }}
                  error={!!errors.name}
                  hiddenLabel
                  disabled={isFormLoading}
                />
                {errors.name && (
                  <span
                    className="error-message"
                    style={{
                      color: "red",
                      fontSize: "12px",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {errors.name}
                  </span>
                )}
              </div>

              <div className="control_group w_50">
                <label>
                  Skill Name {translate?.main?.arabic || "Arabic"}
                </label>
                <TextField
                  placeholder="Enter skill name in Arabic"
                  fullWidth
                  value={nameAr}
                  onChange={(e) => {
                    if (isValidInput(e.target.value)) {
                      setNameAr(e.target.value);
                      setErrors((prev) => ({ ...prev, nameAr: "" }));
                    }
                  }}
                  error={!!errors.nameAr}
                  hiddenLabel
                  disabled={isFormLoading}
                />
                {errors.nameAr && (
                  <span
                    className="error-message"
                    style={{
                      color: "red",
                      fontSize: "12px",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {errors.nameAr}
                  </span>
                )}
              </div>

              <div className="footer_btn_flex">
                <Button 
                  className="btn btn_grey" 
                  onClick={handleClose}
                  disabled={isFormLoading}
                >
                  {translate?.main?.cancel || "Cancel"}
                </Button>
                <Button 
                  className="btn btn_primary" 
                  onClick={handleFormSubmit}
                  disabled={isFormLoading}
                >
                  {selectedId
                    ? translate?.main?.update || "Update"
                    : translate?.globals?.submit || "Submit"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
